import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { featuresData, builtDifferentCopy } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];
const N = featuresData.length;

export const BuiltDifferent = () => {
  const [activeIdx, setActiveIdx] = useState(0);

  return (
    <section
      data-testid="built-different-section"
      className="border-t border-[var(--strollee-line)]/70 px-6 lg:px-10 py-20 lg:py-28"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex items-end justify-between gap-12"
      >
        <div className="max-w-[560px]">
          <p
            data-testid="built-different-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]"
          >
            {builtDifferentCopy.eyebrow}
          </p>
          <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-ink)]">
            {builtDifferentCopy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>
        <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[var(--strollee-muted)]">
          {builtDifferentCopy.support}
        </p>
      </motion.div>

      <div className="mt-14 lg:mt-20 grid grid-cols-12 gap-10 lg:gap-16">
        <div className="col-span-5">
          {featuresData.map((f, i) => {
            const isActive = i === activeIdx;
            return (
              <motion.div
                key={f.id}
                data-testid={`feature-story-${f.id}`}
                onViewportEnter={() => setActiveIdx(i)}
                viewport={{ margin: "-45% 0px -45% 0px" }}
                className={`flex min-h-[52vh] flex-col justify-center transition-opacity duration-500 ${
                  isActive ? "opacity-100" : "opacity-30"
                }`}
              >
                <p className="flex items-baseline gap-3 text-[13px] font-semibold tracking-[0.18em]">
                  <span className={isActive ? "text-[var(--strollee-accent)]" : "text-[var(--strollee-muted)]"}>
                    {f.number}
                  </span>
                  <span className="text-[var(--strollee-muted)]/60">/ 0{N}</span>
                  <span
                    className={`uppercase transition-colors duration-500 ${
                      isActive ? "text-[var(--strollee-ink)]" : "text-[var(--strollee-muted)]"
                    }`}
                  >
                    {f.label}
                  </span>
                </p>
                <h3
                  data-testid={isActive ? "feature-title-active" : undefined}
                  className="mt-5 text-[34px] lg:text-[44px] font-semibold leading-[1.08] tracking-[-0.025em] text-[var(--strollee-ink)]"
                >
                  {f.headlineLines.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </h3>
                <p className="mt-4 max-w-[400px] text-base lg:text-lg leading-relaxed text-[var(--strollee-muted)]">
                  {f.copy}
                </p>
                {i === N - 1 && (
                  <a
                    href="#s1"
                    data-testid="built-different-explore-cta"
                    className="group mt-8 inline-flex w-fit items-center gap-2 text-sm font-semibold tracking-wide text-[var(--strollee-ink)] transition-colors duration-200 hover:text-[var(--strollee-accent)]"
                  >
                    {builtDifferentCopy.endCta}
                    <ArrowUpRight
                      size={16}
                      strokeWidth={2.2}
                      className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
                    />
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="col-span-7">
          <div className="sticky top-[112px]">
            <div
              data-testid="feature-media-stage"
              className="relative h-[62vh] lg:h-[68vh] overflow-hidden rounded-[var(--strollee-radius-hero)] bg-[var(--strollee-line)]/40"
            >
              {featuresData.map((f, i) => (
                <motion.img
                  key={f.id}
                  src={f.image.url}
                  alt={f.image.alt}
                  data-testid={i === activeIdx ? "feature-media-active" : `feature-media-${f.id}`}
                  initial={false}
                  animate={{
                    opacity: i === activeIdx ? 1 : 0,
                    scale: i === activeIdx ? 1 : 1.03,
                  }}
                  transition={{ duration: 0.7, ease: EASE }}
                  style={f.image.filter ? { filter: f.image.filter } : undefined}
                  className="absolute inset-0 h-full w-full object-cover"
                  draggable={false}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
